import api from './api';
import type {
  Shift,
  ShiftFilters,
  CreateShiftDTO,
  UpdateShiftDTO,
  ShiftStatus,
  ShiftStatistics,
  ShiftDistribution,
  SavedFilter,
  Application
} from '../types';
import { validateShiftData, ensureISOFormat } from '../utils/validationUtils';
import { ApiError } from './api';

// Enhanced shift service with proper types
class ShiftService {
  // Get all shifts with filters
  async getShifts(filters?: ShiftFilters): Promise<{ shifts: Shift[], total: number }> {
    try {
      const response = await api.get('/shifts', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching shifts:', error);
      throw this.formatApiError(error);
    }
  }

  // Get shift by ID
  async getShiftById(id: string): Promise<Shift> {
    try {
      const response = await api.get(`/shifts/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching shift with id ${id}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Get hospital shifts with filters
  async getHospitalShifts(hospitalId: string, filters?: ShiftFilters): Promise<{ shifts: Shift[], total: number }> {
    try {
      const response = await api.get(`/shifts/hospital/${hospitalId}`, { params: filters });
      return response.data;
    } catch (error) {
      console.error(`Error fetching shifts for hospital ${hospitalId}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Create shift
  async createShift(shiftData: CreateShiftDTO): Promise<Shift> {
    try {
      // 유효성 검사 - 모든 필수 필드 확인
      const validationError = validateShiftData(shiftData);
      if (validationError) {
        throw { 
          message: validationError, 
          statusCode: 400 
        } as ApiError;
      }

      // 백엔드 API가 기대하는 형식으로 시간 데이터 변환
      // 시작 시간과 종료 시간을 ISO 문자열로 변환
      const formattedData = {
        ...shiftData,
        startTime: this.formatTimeString(shiftData.startDate, shiftData.startTime),
        endTime: this.formatTimeString(shiftData.endDate, shiftData.endTime),
      };
      
      console.debug('Sending formatted shift data to API:', formattedData);
      
      const response = await api.post('/shifts', formattedData);
      return response.data;
    } catch (error) {
      console.error('Error creating shift:', error);
      throw this.formatApiError(error);
    }
  }

  // 날짜와 시간 문자열을 ISO 형식 문자열로 변환하는 유틸리티 메소드
  private formatTimeString(dateStr: string, timeStr: string): string {
    try {
      // 입력값 검증
      if (!dateStr || !timeStr) {
        console.error('Date format error: Missing dateStr or timeStr', { dateStr, timeStr });
        return timeStr;
      }

      // dateStr이 이미 ISO 형식인지 확인
      let formattedDateStr = dateStr;
      if (dateStr.includes('T')) {
        // ISO 형식에서 날짜 부분만 추출
        formattedDateStr = dateStr.split('T')[0];
      }

      // timeStr이 HH:MM 형식인지 확인
      if (!/^\d{2}:\d{2}$/.test(timeStr)) {
        console.error('Date format error: Invalid time format', { timeStr });
        return timeStr;
      }

      // 결합하여 ISO 문자열 생성 "YYYY-MM-DDThh:mm:ss.sssZ"
      const combinedStr = `${formattedDateStr}T${timeStr}:00`;
      const date = new Date(combinedStr);
      
      if (isNaN(date.getTime())) {
        console.error('Date format error: Invalid date created', { combinedStr });
        return timeStr;
      }
      
      return date.toISOString();
    } catch (err) {
      console.error('Date format error:', err);
      // 변환 실패 시 원본 반환
      return timeStr;
    }
  }

  // Create multiple shifts in a batch
  async createShiftBatch(shiftsData: CreateShiftDTO[]): Promise<{ shifts: Shift[], count: number }> {
    try {
      // 각 시프트 데이터의 날짜/시간 형식 변환
      const formattedShifts = shiftsData.map(shiftData => {
        const validationError = validateShiftData(shiftData);
        if (validationError) {
          throw { message: validationError, statusCode: 400 } as ApiError;
        }
        
        return {
          ...shiftData,
          startTime: this.formatTimeString(shiftData.startDate, shiftData.startTime),
          endTime: this.formatTimeString(shiftData.endDate, shiftData.endTime),
        };
      });
      
      const response = await api.post('/shifts/batch', { shifts: formattedShifts });
      return response.data;
    } catch (error) {
      console.error('Error creating shifts batch:', error);
      throw this.formatApiError(error);
    }
  }

  // Update shift - 시간 형식 조정
  async updateShift(id: string, shiftData: UpdateShiftDTO): Promise<Shift> {
    try {
      // 클라이언트 측 데이터 형식 조정
      const formattedData = { ...shiftData };
      
      // 시간 필드가 있는 경우에만 ISO 문자열로 변환
      if (formattedData.startTime && formattedData.startDate) {
        // startTime이 이미 ISO 형식인지 확인
        if (!formattedData.startTime.includes('T')) {
          formattedData.startTime = this.formatTimeString(formattedData.startDate, formattedData.startTime);
        }
      }
      
      if (formattedData.endTime && formattedData.endDate) {
        // endTime이 이미 ISO 형식인지 확인
        if (!formattedData.endTime.includes('T')) {
          formattedData.endTime = this.formatTimeString(formattedData.endDate, formattedData.endTime);
        }
      }

      // 날짜 필드가 있는 경우 ISO 형식으로 변환
      if (formattedData.startDate && !formattedData.startDate.includes('T')) {
        formattedData.startDate = new Date(formattedData.startDate + 'T00:00:00.000Z').toISOString();
      }
      
      if (formattedData.endDate && !formattedData.endDate.includes('T')) {
        formattedData.endDate = new Date(formattedData.endDate + 'T00:00:00.000Z').toISOString();
      }
      
      // 디버깅용 로그 추가
      if (process.env.NODE_ENV === 'development') {
        console.log('=== SHIFT UPDATE DEBUG ===');
        console.log('Original shift data:', shiftData);
        console.log('Formatted data being sent:', formattedData);
        console.log('API endpoint:', `/shifts/${id}`);
      }

      const response = await api.put(`/shifts/${id}`, formattedData);
      if (process.env.NODE_ENV === 'development') {
        console.log('API response:', response.data);
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating shift with id ${id}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Delete shift
  async deleteShift(id: string): Promise<boolean> {
    try {
      await api.delete(`/shifts/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting shift with id ${id}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Update shift status
  async updateShiftStatus(id: string, status: ShiftStatus): Promise<Shift> {
    try {
      const response = await api.patch(`/shifts/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating status for shift with id ${id}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Batch update shift statuses
  async updateShiftStatusBatch(ids: string[], status: ShiftStatus): Promise<{ count: number }> {
    try {
      const response = await api.patch(`/shifts/status/batch`, { ids, status });
      return response.data;
    } catch (error) {
      console.error(`Error updating status for multiple shifts:`, error);
      throw this.formatApiError(error);
    }
  }

  // Reschedule shift (update times)
  async rescheduleShift(id: string, startTime: string, endTime: string): Promise<Shift> {
    try {
      const response = await api.put(`/shifts/${id}/reschedule`, { 
        startTime: startTime.includes('T') ? startTime : this.formatTimeString('2000-01-01', startTime),
        endTime: endTime.includes('T') ? endTime : this.formatTimeString('2000-01-01', endTime) 
      });
      
      // 백엔드 응답 형식 처리: { success: true, data: updatedShift } 또는 직접 시프트 객체
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error rescheduling shift with id ${id}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Get shift applicants
  async getShiftApplicants(shiftId: string): Promise<Application[]> {
    try {
      const response = await api.get(`/shifts/${shiftId}/applications`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching applicants for shift ${shiftId}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Search shifts with filters
  async searchShifts(filters?: ShiftFilters): Promise<{ shifts: Shift[], total: number }> {
    try {
      const response = await api.get('/shifts/search', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error searching shifts:', error);
      throw this.formatApiError(error);
    }
  }

  // Get shift statistics
  async getShiftStatistics(hospitalId: string, filters?: ShiftFilters): Promise<ShiftStatistics> {
    try {
      const response = await api.get(`/shifts/statistics/${hospitalId}`, { params: filters });
      return response.data;
    } catch (error) {
      console.error(`Error fetching statistics for hospital ${hospitalId}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Get shift distribution
  async getShiftDistribution(hospitalId: string, filters?: ShiftFilters): Promise<ShiftDistribution> {
    try {
      const response = await api.get(`/shifts/distribution/${hospitalId}`, { params: filters });
      return response.data;
    } catch (error) {
      console.error(`Error fetching distribution for hospital ${hospitalId}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Get saved filters
  async getSavedFilters(): Promise<{ savedFilters: SavedFilter[] }> {
    try {
      const response = await api.get('/shifts/filters/saved');
      return response.data;
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      throw this.formatApiError(error);
    }
  }

  // Save filter
  async saveFilter(name: string, filters: ShiftFilters): Promise<SavedFilter> {
    try {
      const response = await api.post('/shifts/filters/saved', { name, filters });
      return response.data;
    } catch (error) {
      console.error('Error saving filter:', error);
      throw this.formatApiError(error);
    }
  }

  // Delete saved filter
  async deleteFilter(id: string): Promise<void> {
    try {
      await api.delete(`/shifts/filters/saved/${id}`);
    } catch (error) {
      console.error(`Error deleting filter ${id}:`, error);
      throw this.formatApiError(error);
    }
  }

  // Calculate shift metrics (hours, cost, etc)
  async calculateShiftMetrics(shiftData: Partial<CreateShiftDTO>): Promise<{ totalHours: number, totalPay: number, totalShifts: number }> {
    try {
      // 계산을 위한 데이터 형식 변환
      const formattedData = { 
        ...shiftData
      };
      
      // 시간 필드가 있는 경우 ISO 문자열로 변환
      if (shiftData.startTime && shiftData.startDate) {
        formattedData.startTime = this.formatTimeString(shiftData.startDate, shiftData.startTime);
      }
      
      if (shiftData.endTime && shiftData.endDate) {
        formattedData.endTime = this.formatTimeString(shiftData.endDate, shiftData.endTime);
      }
      
      const response = await api.post('/shifts/calculate', formattedData);
      return response.data;
    } catch (error) {
      console.error('Error calculating shift metrics:', error);
      throw this.formatApiError(error);
    }
  }
  
  // API 오류 형식화 헬퍼 메서드
  private formatApiError(error: any): ApiError {
    if (error.statusCode) return error as ApiError;
    
    return {
      message: error.message || 'Unknown error',
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors
    };
  }
}

export const shiftService = new ShiftService();
export default shiftService; 